import { X, RefreshCw, Cloud, ExternalLink, CheckCircle, XCircle, Plus, Edit2, Trash2, Save } from 'lucide-react';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import tenantService from '@/services/tenantService';
import SearchInputDebounced from '../ui/SearchInputDebounced';
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal';

interface DNSRecord {
  id: string;
  type: string;
  name: string;
  content: string;
  proxied: boolean;
  ttl: number;
  zone_name: string;
  created_on: string;
  modified_on: string;
}

interface CloudFlareDNSManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  records: DNSRecord[];
  isLoading?: boolean;
  onRefresh: () => void;
}

interface DNSFormData {
  type: string;
  name: string;
  content: string;
  ttl: number;
  proxied: boolean;
}

export default function CloudFlareDNSManagerModal({
  isOpen,
  onClose,
  records,
  isLoading = false,
  onRefresh
}: CloudFlareDNSManagerModalProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DNSRecord | null>(null);
  const [formData, setFormData] = useState<DNSFormData>({
    type: 'CNAME',
    name: '',
    content: 'lb.skillzen.ai',
    ttl: 3600,
    proxied: true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [recordToDelete, setRecordToDelete] = useState<DNSRecord | null>(null);

  // Filter records based on search query
  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) {
      return records;
    }

    const query = searchQuery.toLowerCase();
    return records.filter((record) =>
      record.name.toLowerCase().includes(query) ||
      record.type.toLowerCase().includes(query) ||
      record.content.toLowerCase().includes(query)
    );
  }, [records, searchQuery]);

  if (!isOpen) return null;

  const handleAddNew = () => {
    setEditingRecord(null);
    setFormData({
      type: 'CNAME',
      name: '',
      content: 'lb.skillzen.ai',
      ttl: 3600,
      proxied: true,
    });
    setShowForm(true);
  };

  const handleEdit = (record: DNSRecord) => {
    setEditingRecord(record);
    setFormData({
      type: record.type,
      name: record.name,
      content: record.content,
      ttl: record.ttl,
      proxied: record.proxied,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast.error('Name and content are required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRecord) {
        // Update existing record
        await tenantService.updateDNSRecord(editingRecord.id, formData);
        toast.success('DNS record updated successfully');
      } else {
        // Create new record
        await tenantService.createDNSRecord(formData);
        toast.success('DNS record created successfully');
      }
      setShowForm(false);
      onRefresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save DNS record');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (record: DNSRecord) => {
    setRecordToDelete(record);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;

    setDeletingId(recordToDelete.id);
    try {
      await tenantService.deleteDNSRecord(recordToDelete.id);
      toast.success('DNS record deleted successfully');
      setRecordToDelete(null);
      onRefresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete DNS record');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => !showForm && onClose()}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Cloud className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">CloudFlare DNS Manager</h2>
                <p className="text-sm text-gray-500">View and manage DNS records</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!showForm && (
                <>
                  <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Record</span>
                  </button>
                  <button
                    onClick={onRefresh}
                    disabled={isLoading}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setShowForm(false);
                  onClose();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {/* Search Input - Only show when not in form mode */}
            {!showForm && !isLoading && records.length > 0 && (
              <div className="mb-4">
                <SearchInputDebounced
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search by hostname, type, or target..."
                  searchBy="hostname, type, target"
                  className="max-w-md"
                />
              </div>
            )}

            {showForm ? (
              /* Add/Edit Form */
              <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {editingRecord ? 'Edit DNS Record' : 'Add New DNS Record'}
                </h3>

                <div className="space-y-4">
                  {/* Record Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Record Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={isSaving}
                    >
                      <option value="A">A - IPv4 Address</option>
                      <option value="AAAA">AAAA - IPv6 Address</option>
                      <option value="CNAME">CNAME - Canonical Name</option>
                      <option value="TXT">TXT - Text</option>
                      <option value="MX">MX - Mail Exchange</option>
                    </select>
                  </div>

                  {/* Hostname */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hostname <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., subdomain.skillzen.ai"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
                      disabled={isSaving}
                    />
                  </div>

                  {/* Content/Target */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content/Target <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="e.g., lb.skillzen.ai or 192.168.1.1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
                      disabled={isSaving}
                    />
                  </div>

                  {/* TTL and Proxied */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        TTL (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.ttl}
                        onChange={(e) => setFormData({ ...formData, ttl: parseInt(e.target.value) || 3600 })}
                        min="1"
                        max="86400"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        disabled={isSaving}
                      />
                      <p className="text-xs text-gray-500 mt-1">1 = Auto, 3600 = 1 hour</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proxied (CloudFlare CDN)
                      </label>
                      <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.proxied}
                          onChange={(e) => setFormData({ ...formData, proxied: e.target.checked })}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500 rounded"
                          disabled={isSaving}
                        />
                        <span className="text-sm text-gray-700">Enable Proxy</span>
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-4">
                    <button
                      onClick={() => setShowForm(false)}
                      disabled={isSaving}
                      className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving || !formData.name.trim() || !formData.content.trim()}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>{editingRecord ? 'Update Record' : 'Create Record'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 text-orange-600 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading DNS records...</p>
                </div>
              </div>
            ) : records.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Cloud className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No DNS records found</p>
                  <p className="text-gray-400 text-sm mt-1">Click "Add Record" to create one</p>
                </div>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Cloud className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No DNS records match your search</p>
                  <p className="text-gray-400 text-sm mt-1">Try different search terms</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Hostname</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Target</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Proxied</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">TTL</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((record) => (
                      <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {record.type}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-900">{record.name}</span>
                            <a
                              href={`https://${record.name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700"
                              title="Open in new tab"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm text-gray-600">{record.content}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {record.proxied ? (
                            <div className="flex items-center justify-center gap-1">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-green-700 font-medium">Yes</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1">
                              <XCircle className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-500">No</span>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-sm text-gray-600">
                            {record.ttl === 1 ? 'Auto' : `${record.ttl}s`}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(record)}
                              className="p-1.5 hover:bg-blue-50 text-blue-600 rounded transition-colors"
                              title="Edit record"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(record)}
                              disabled={deletingId === record.id}
                              className="p-1.5 hover:bg-red-50 text-red-600 rounded transition-colors disabled:opacity-50"
                              title="Delete record"
                            >
                              {deletingId === record.id ? (
                                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              {showForm ? (
                editingRecord ? 'Editing DNS record' : 'Adding new DNS record'
              ) : searchQuery ? (
                `Showing ${filteredRecords.length} of ${records.length} DNS records`
              ) : (
                `Total DNS Records: ${records.length}`
              )}
            </p>
            <button
              onClick={() => {
                setShowForm(false);
                onClose();
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!recordToDelete}
        onClose={() => setRecordToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete DNS Record"
        message={`Are you sure you want to delete the DNS record for "${recordToDelete?.name}"? This action cannot be undone.`}
        itemName={recordToDelete?.name || ''}
      />
    </div>
  );
}
